import { useDisclosure } from '@mantine/hooks';
import ChangeDeviceRemoteSourceModal, {
  ChangeDeviceRemoteSourceModalProps,
} from 'components/modals/change-device-remote-source/ChangeDeviceRemoteSourceModal';
import { createContext, useContext, useMemo, useState } from 'react';

type OpenChangeDeviceRemoteSourceModalParams = Omit<ChangeDeviceRemoteSourceModalProps, 'opened' | 'onClose'>;
type OpenChangeDeviceRemoteSourceModal = (params: OpenChangeDeviceRemoteSourceModalParams) => void;

interface IChangeDeviceRemoteSourceModalContext {
  openChangeDeviceRemoteSourceModal: OpenChangeDeviceRemoteSourceModal;
}

const ChangeDeviceRemoteSourceModalContext = createContext<IChangeDeviceRemoteSourceModalContext>(undefined!);

/**
 * Provider for opening the change device remote source modal.
 */
export function ChangeDeviceRemoteSourceModalProvider({ children }: { children: React.ReactNode }) {
  const [opened, { close, open }] = useDisclosure(false);
  const [params, setParams] = useState<OpenChangeDeviceRemoteSourceModalParams | undefined>(undefined);

  /**
   * Opens the modal for changing a device remote source.
   */
  const openChangeDeviceRemoteSourceModal: OpenChangeDeviceRemoteSourceModal = (nextParams) => {
    setParams(nextParams);
    open();
  };

  const value = useMemo(() => ({ openChangeDeviceRemoteSourceModal }), [openChangeDeviceRemoteSourceModal]);

  return (
    <ChangeDeviceRemoteSourceModalContext.Provider value={value}>
      {children}
      {opened && <ChangeDeviceRemoteSourceModal opened onClose={close} {...params!} />}
    </ChangeDeviceRemoteSourceModalContext.Provider>
  );
}

/**
 * Hook for opening the change device remote source modal.
 */
export function useChangeDeviceRemoteSourceModal() {
  return useContext(ChangeDeviceRemoteSourceModalContext);
}
